import cn from 'classnames';
import * as React from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperProps } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { NavigationOptions } from 'swiper/types';

import { NavigationButton, NavigationButtonDir } from './NavigationButton';
import s from './Slider.module.scss';

type SliderProps = {
  spaceBetween: SwiperProps['spaceBetween'];
  children: SwiperProps['children'];
  className?: string;
  slidesPerView: SwiperProps['slidesPerView'];

  onSwiper?: SwiperProps['onSwiper'];
  breakpoints?: SwiperProps['breakpoints'];
};

const Slider: React.FC<SliderProps> = ({
  children,
  onSwiper,
  ...swiperProps
}) => {
  const leftButtonRef = React.useRef<HTMLElement>();

  const rightButtonRef = React.useRef<HTMLElement>();

  const handleSwiper = React.useCallback(
    (swiper) => {
      onSwiper?.(swiper);
      setTimeout(() => {
        const navigationParams = swiper.params.navigation as NavigationOptions;

        // Override prevEl & nextEl now that refs are defined
        navigationParams.prevEl = leftButtonRef.current;
        navigationParams.nextEl = rightButtonRef.current;

        // Re-init navigation
        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();
      });
    },
    [onSwiper]
  );

  const navigationOptions = React.useRef<NavigationOptions>({
    prevEl: leftButtonRef.current,
    nextEl: rightButtonRef.current,
  });

  return (
    <div className={s['slider']}>
      <Swiper
        {...swiperProps}
        modules={[Navigation]}
        navigation={navigationOptions.current}
        onSwiper={handleSwiper}
      >
        {children}
      </Swiper>
      <NavigationButton
        ref={leftButtonRef as React.RefObject<HTMLElement>}
        className={cn(s['slider__nav'], s['slider__nav_left'])}
      />
      <NavigationButton
        dir={NavigationButtonDir.right}
        className={cn(s['slider__nav'], s['slider__nav_right'])}
        ref={rightButtonRef as React.RefObject<HTMLElement>}
      />
    </div>
  );
};

export default Slider;
